import React, { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useLoginModal } from '../context/LoginModalContext';
import LanguageSwitcher from './LanguageSwitcher';
import API from '../utils/api';

const DEFAULT_SITE_NAME = 'Sri Bramaramba Mallikarjuna Swami Goshala';
const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

export default function Navbar() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { openLogin } = useLoginModal();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userDropdown, setUserDropdown] = useState(false);
  const [siteName, setSiteName] = useState(DEFAULT_SITE_NAME);
  const [logoImage, setLogoImage] = useState('');
  const [menuItems, setMenuItems] = useState([]);

  useEffect(() => {
    API.get('/settings').then(r => {
      const name = r.data?.siteName || DEFAULT_SITE_NAME;
      setSiteName(name);
      document.title = name;
      setLogoImage(r.data?.logoImage || '');
    }).catch(() => {});
    API.get('/menu-items').then(r => setMenuItems(r.data)).catch(() => {});
  }, []);

  const logoImageUrl = logoImage ? (logoImage.startsWith('http') ? logoImage : `${API_BASE}${logoImage}`) : null;

  const handleLogout = async () => {
    await logout();
    setUserDropdown(false);
    navigate('/');
  };

  return (
    <nav className="navbar">
      <div className="tricolor-strip" />
      <div className="navbar-inner">
        <Link to="/" className="navbar-logo">
          {logoImageUrl
            ? <img src={logoImageUrl} alt={siteName} className="navbar-logo-badge navbar-logo-badge-img" />
            : <span className="navbar-logo-badge">🐂</span>
          }
          <div className="navbar-logo-text">
            <span className="navbar-logo-name">{siteName}</span>
            <span className="navbar-logo-tagline">{t('nav.shelterTagline')}</span>
          </div>
        </Link>

        <div className={`navbar-menu${menuOpen ? ' open' : ''}`}>
          <div className="navbar-links">
            {menuItems.filter(item => !item.requiresAuth || user).map(item => (
              item.path.startsWith('http')
                ? <a key={item._id} href={item.path} target={item.openInNewTab ? '_blank' : undefined} rel={item.openInNewTab ? 'noopener noreferrer' : undefined} onClick={() => setMenuOpen(false)}>{item.label}</a>
                : <NavLink key={item._id} to={item.path} onClick={() => setMenuOpen(false)}>{item.label}</NavLink>
            ))}
          </div>

          <div className="navbar-actions">
            <LanguageSwitcher />

            <NavLink to="/donations" className="navbar-action-btn navbar-action-btn-filled" style={{ textDecoration: 'none' }} onClick={() => setMenuOpen(false)}>
              {t('nav.donate')}
            </NavLink>

            {user ? (
              <div className="navbar-action-btn navbar-action-btn-tinted navbar-user" onClick={() => setUserDropdown(!userDropdown)}>
                <span>👤</span>
                <span style={{ maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.name?.split(' ')[0] || t('nav.userFallback')}
                </span>
                <span>▾</span>
                {userDropdown && (
                  <div style={{
                    position: 'absolute', top: '100%', right: 0, marginTop: 4,
                    background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)', minWidth: 180, zIndex: 1000
                  }}>
                    <Link to="/my-donations" style={{ display: 'block', padding: '12px 16px', color: '#444', fontSize: '0.9rem' }} onClick={() => { setUserDropdown(false); setMenuOpen(false); }}>
                      {t('nav.myDonationsMenu')}
                    </Link>
                    <div onClick={handleLogout} style={{ padding: '12px 16px', color: '#c62828', fontSize: '0.9rem', cursor: 'pointer', borderTop: '1px solid #f0f0f0' }}>
                      {t('nav.logout')}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <button type="button" className="navbar-action-btn navbar-action-btn-outline" onClick={() => { openLogin(); setMenuOpen(false); }}>{t('nav.login')}</button>
            )}
          </div>
        </div>

        <span className="navbar-hamburger" onClick={() => setMenuOpen(!menuOpen)}>
          {menuOpen ? '✕' : '☰'}
        </span>
      </div>
    </nav>
  );
}
